import { useAuthContext } from '@experiences/util';
import {
    useCallback,
    useMemo,
} from 'react';
import useSWRInfinite from 'swr/infinite';

import type { CodedDashboard } from '../../../../../services/governanceV2/CodedDashboardsService';
import { fetchCodedDashboardsPage } from '../../../../../services/governanceV2/CodedDashboardsService';
import { useTenantsContext } from '../../../../tenants/TenantsContextProvider';

const PICKER_PAGE_SIZE = 20;

interface DashboardPicker {
    dashboards: CodedDashboard[];
    /** First read only. Distinguishes "still arriving" from "nothing to pin", which look identical. */
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
    /** Re-reads the pages loaded so far. The shared `refresh` cannot: a key filter skips paged queries. */
    refresh: () => Promise<unknown>;
}

/**
 * Every governance dashboard, a page at a time, for the pin picker.
 *
 * The picker is the one surface that genuinely lists all of them, so it pages on scroll instead of
 * draining upfront. Search is server-side, because picking one of two hundred by scrolling is worse
 * than typing three letters.
 *
 * `isOpen` gates the request. The menu mounts with the nav rather than with its popover, so without
 * this the tenant-wide listing is fetched on every governance page load — the cost the nav's
 * pinned-only query exists to avoid — for a menu most users never open.
 */
export function useDashboardPicker(searchText: string, isOpen: boolean): DashboardPicker {
    const { token } = useAuthContext();
    const { selectedTenant } = useTenantsContext();
    const tenantId = selectedTenant.id;

    const isReady = Boolean(token && tenantId && isOpen);
    const search = searchText.trim();

    const {
        data, isLoading, isValidating, size, setSize, mutate,
    } = useSWRInfinite(
        (index: number) => (isReady ? [ 'coded-dashboards-picker', tenantId, search, index ] as const : null),
        ([ , id, searchTerm, index ]) => fetchCodedDashboardsPage(id, {
            top: PICKER_PAGE_SIZE,
            skip: index * PICKER_PAGE_SIZE,
            searchText: searchTerm || undefined,
        }),
        {
            revalidateOnFocus: false,
            revalidateFirstPage: false,
        },
    );

    const dashboards = useMemo(() => (data ?? []).flatMap(page => page.dashboards), [ data ]);
    const total = data?.[0]?.total ?? 0;

    // Rows asked for, not rows returned — the mapper drops some, which would make this true forever.
    const hasMore = size * PICKER_PAGE_SIZE < total;

    const loadMore = useCallback(() => {
        if (hasMore && !isValidating) {
            setSize(current => current + 1);
        }
    }, [ hasMore, isValidating, setSize ]);

    return {
        dashboards,
        // Gated shut reads as loading: the menu is about to open and ask, so an empty state would
        // only flash before the rows land.
        isLoading: !isReady || (isLoading && !data),
        hasMore,
        loadMore,
        refresh: mutate,
    };
}
